import React, { memo, createContext, useReducer, useContext, useMemo, Fragment, useState } from "react";
import AppModal from "./AppModal";
import getErrorMessage from "../utilities/getErrorMessage";
import axiosInstance from "../utilities/axiosInstance";
import AppModalAlertMessage from "./AppModalAlertMessage";

const ObatIcon = require("../../assets/images/dummyobat.svg");

//#region stores
const actionName = {
	INCREMENT_QUANTITY: "INCREMENT_QUANTITY",
	DECREMENT_QUANTITY: "DECREMENT_QUANTITY",
	TOGGLE_CHECKED: "TOGGLE_CHECKED",
};

const recipesAction = {
	incrementQuantity: (index) => ({ type: actionName.INCREMENT_QUANTITY, payload: index }),
	decrementQuantity: (index) => ({ type: actionName.DECREMENT_QUANTITY, payload: index }),
	toggleChecked: (index) => ({ type: actionName.TOGGLE_CHECKED, payload: index }),
};

const recipesReducer = (state, action) => {
	switch (action.type) {
		case actionName.INCREMENT_QUANTITY:
			return {
				...state,
				medicines: [
					...state.medicines.slice(0, action.payload),
					{
						...state.medicines[action.payload],
						attributes: {
							...state.medicines[action.payload].attributes,
							quantity: state.medicines[action.payload].attributes.quantity + 1,
						},
					},
					...state.medicines.slice(action.payload + 1),
				],
			};

		case actionName.DECREMENT_QUANTITY:
			return {
				...state,
				medicines: [
					...state.medicines.slice(0, action.payload),
					{
						...state.medicines[action.payload],
						attributes: {
							...state.medicines[action.payload].attributes,
							quantity: state.medicines[action.payload].attributes.quantity - 1,
						},
					},
					...state.medicines.slice(action.payload + 1),
				],
			};

		case actionName.TOGGLE_CHECKED:
			return {
				...state,
				medicines: [
					...state.medicines.slice(0, action.payload),
					{
						...state.medicines[action.payload],
						checked: !state.medicines[action.payload].checked,
					},
					...state.medicines.slice(action.payload + 1),
				],
			};

		default:
			return state;
	}
};

const DoctorRecipesContext = createContext(null);
//#endregion stores

const DoctorRecipes = ({ medicines, recipe }) => {
	const initialState = {
		medicines: medicines.data.map((m) => ({
			...m,
			checked: true,
			attributes: {
				...m.attributes,
				price: parseFloat(m.attributes.price),
				maxQuantity: m.attributes.quantity,
			},
		})),
		recipe,
	};
	const [state, dispatch] = useReducer(recipesReducer, initialState);

	return (
		<DoctorRecipesContext.Provider value={{ state, dispatch }}>
			<DoctorRecipeList />
			<DoctorRecipeActionBar />
		</DoctorRecipesContext.Provider>
	);
};

export default DoctorRecipes;

const DoctorRecipeActionBar = () => {
	const {
		state: {
			medicines,
			recipe: { id: recipe_id },
		},
	} = useContext(DoctorRecipesContext);
	const [loading, setLoading] = useState(false);
	const [alertMessage, setAlertMessage] = useState({ success: null, error: null });
	const selectedmedicines = useMemo(() => medicines.filter((m) => m.checked), [medicines]);
	const totalPrice = useMemo(
		() => selectedmedicines.reduce((acc, m) => (acc += m.attributes.price * m.attributes.quantity), 0),
		[selectedmedicines]
	);

	const onSubmit = async () => {
		setLoading(true);
		try {
			const request = {
				items: selectedmedicines.map(
					({ attributes: { name, quantity, quantity_unit, dosage, dosage_unit, price } }) => ({
						name,
						quantity,
						quantity_unit,
						dosage,
						dosage_unit,
						price: price * quantity,
					})
				),
			};
			const { data } = await axiosInstance.post(`/api/patient/recipes/${recipe_id}/invoice`, request);

			if (data?.success) {
				setAlertMessage({ success: data.message, error: null });
				setTimeout(() => Turbolinks.visit(data.redirect_url), 1000);
			}
		} catch (err) {
			const error = getErrorMessage(err);
			setAlertMessage({ success: null, error });
		} finally {
			setLoading(false);
		}
	};

	return (
		<Fragment>
			<div className="position-fixed-bottom fixed-bg mx-auto" style={{ left: 0, right: 0 }}>
				<div className="d-flex w-100">
					<div className="price-box">
						<div className="text-total-harga">Total Harga</div>
						<div className="duit">Rp {totalPrice.toPriceFormat()}</div>
					</div>
					<button
						className="btn btn-primary btn-lg btn-block beli"
						disabled={selectedmedicines.length === 0 || loading}
						onClick={onSubmit}
					>
						{loading ? "Processing..." : `Beli (${selectedmedicines.length})`}
					</button>
				</div>
			</div>
			<AppModalAlertMessage message={alertMessage} setMessage={setAlertMessage} />
		</Fragment>
	);
};

const DoctorRecipeList = () => {
	const {
		state: { medicines },
	} = useContext(DoctorRecipesContext);
	return (
		<div className="input-list mb-6">
			{medicines.map((m, i) => (
				<DoctorRecipeListItem key={m.id} index={i} {...m} />
			))}
			<div className="custom-control custom-checkbox mb-1">
        <input type="checkbox" name="agreement" className="custom-control-input" id="customCheckb1" required="required"/>
        <label className="custom-control-label text-left" for="customCheckb1">Saya sudah membaca dan mengerti ketentuan penggunaan obat</label>
      </div>
		</div>
	);
};

const DoctorRecipeListItem = memo((props) => {
	const totalPrice = props.attributes.price * props.attributes.quantity;
	return (
		<div className="chekout-box-product mt-3">
			<DoctorRecipeListItemCheckBox index={props.index} checked={props.checked} />
			<div className="item chekout">
				<div className="imageWrapper">
					<img className="imaged w64" src={ObatIcon} />
				</div>
				<div className="in">
					<p>{props.attributes.name}</p>
					<div className="d-flex justify-content-between">
						<span>
							{props.attributes.quantity} {props.attributes.quantity_unit}
						</span>
						<DoctorRecipeListItemCounter
							index={props.index}
							quantity={props.attributes.quantity}
							maxValue={props.attributes.maxQuantity}
						/>
					</div>
					<strong>Rp {totalPrice.toPriceFormat()}</strong>
					<div className="float-right">
						<DoctorRecipeListItemDetail {...props} />
					</div>
				</div>
			</div>
		</div>
	);
});

const DoctorRecipeListItemDetail = ({ attributes }) => {
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const onRequestClose = () => setIsDetailOpen(false);
	return (
		<Fragment>
			<button
				className="btn btn-small btn-outline-primary rounded"
				type="button"
				onClick={() => setIsDetailOpen(true)}
			>
				Details
			</button>
			<AppModal
				isActionSheet
				isOpen={isDetailOpen}
				onRequestClose={onRequestClose}
				overlayClassName="modalbox"
				modalHeader={
					<div className="d-flex justify-content-between align-items-center w-100">
						<h3 className="m-0">Detail Obat</h3>
						<a className="cursor-pointer" onClick={onRequestClose} style={{ fontSize: "26px" }}>
							<ion-icon name="close" role="img" className="md hydrated" aria-label="close" />
						</a>
					</div>
				}
			>
				<div className="row">
					<div className="mb-2 w-100">
						<h5>{attributes.name}</h5>
						<p className="mb4 d-flex justify-content-between">
							Quantity
							<span>
								{attributes.quantity} {attributes.quantity_unit}
							</span>
						</p>
						<p className="mb4 d-flex justify-content-between">
							Dosage
							<span>
								{attributes.dosage} {attributes.dosage_unit}
							</span>
						</p>
						<p className="mb4 d-flex justify-content-between">
							Frequency (times a day)<span>{attributes.frequency}</span>
						</p>
						<p className="mb4 d-flex justify-content-between">
							When to use<span>{attributes.when_to_use[0] && attributes.when_to_use.join(", ")}</span>
						</p>
					</div>
					{attributes.note && (
						<div className="box-notes w-100">
							<h5>Notes</h5>
							<span className="font12">{attributes.note}</span>
						</div>
					)}
				</div>
			</AppModal>
		</Fragment>
	);
};

const DoctorRecipeListItemCheckBox = (props) => {
	const { dispatch } = useContext(DoctorRecipesContext);
	const onChange = () => {
		dispatch(recipesAction.toggleChecked(props.index));
	};

	return (
		<label className="control control--checkbox">
			<input type="checkbox" checked={props.checked} onChange={onChange} />
			<div className="control__indicator" />
		</label>
	);
};

const DoctorRecipeListItemCounter = (props) => {
	const { dispatch } = useContext(DoctorRecipesContext);
	const isMax = useMemo(() => props.quantity >= props.maxValue, [props.maxValue, props.quantity]);

	const decrement = () => {
		if (props.quantity <= 1) {
			return;
		}

		dispatch(recipesAction.decrementQuantity(props.index));
	};

	const increment = () => {
		if (isMax) {
			return;
		}

		dispatch(recipesAction.incrementQuantity(props.index));
	};

	return (
		<div className="quantity">
			<input className="minus" type="button" defaultValue="-" onClick={decrement} />
			<input className="qty" name="quantity" title="Qty" type="text" value={props.quantity} readOnly />
			<input className="plus" type="button" defaultValue="+" onClick={increment} disabled={isMax} />
		</div>
	);
};

DoctorRecipeListItemCounter.defaultProps = {
	maxValue: 10,
};
